import { container } from "@graphistry/falcor-react-redux";
import { Observable } from "rxjs";
import { Inspector, MultiInspector } from "./InspectorComponents";
import {
  addFilter,
  addExclusion,
} from "@graphistry/viz/src/actions/expressions";
import {
  setExpanderOpen,
  setExpanderHops,
  setExpanderDirection,
  commitExpansion,
} from "@graphistry/viz/src/actions/expander";
import { pointColors } from "@graphistry/viz/src/client/legacy";
import { mapPropsStream } from "react-recompose";
import { filterLabelProperties, processEncodingsComputed, processEncodingsNulls } from '../../utils/encodingUtils';

// changing up the pattern a little bit here - containers here import their components and wrap them
// here directly, rather than having the wrapping happen in index.js
const WithColorData = mapPropsStream((propStream) =>
  Observable.from(propStream)
    .withLatestFrom(
      pointColors.map(
        ({ buffer, byteOffset, byteLength }) =>
          new Uint8Array(
            buffer,
            byteOffset,
            byteLength / Uint8Array.BYTES_PER_ELEMENT
          )
      )
    )
    .map(([props, colorData]) => ({
      ...props, 
      getColorForIndex: (index) => colorData.subarray(index * 4, index * 4 + 3),
    }))
);


const WithFilteredColumns = mapPropsStream((propStream) => 
  Observable.from(propStream)
    .map((props) => {
      console.log('Initial props:', {
        type: props.type,
        columns: props.columns,
        encodings: props.encodings?.complex?.edge?.bindings
      });

      const labelData = {
        type: props.type,
        index: props.index,
        globalIndex: props.globalIndex,
        columns: props.columns || [],
        title: props.title
      };

      const hasCustomSource = props.columns?.some(col => col.key === 's');
      const hasCustomDest = props.columns?.some(col => col.key === 'd');

      const sourceField = hasCustomSource ? 's' : 'source';
      const destField = hasCustomDest ? 'd' : 'destination';

      const baseEssentialColumns = ['id', '_id', 'title', 'name', 'identifier'];
      const essentialColumns = props.type === 'edge' 
        ? [
            ...baseEssentialColumns,
            sourceField,
            destField
          ]
        : baseEssentialColumns;

      const processedLabels = filterLabelProperties(props.encodings, [labelData]);
      let finalProcessedLabel = processEncodingsComputed(props.encodings, processedLabels)[0];
      finalProcessedLabel = processEncodingsNulls(props.encodings, [finalProcessedLabel])[0];

      if (finalProcessedLabel?.columns) {
        
        finalProcessedLabel.columns = finalProcessedLabel.columns
          .filter(col => {
            if (props.type === 'edge') {
              if (hasCustomSource && col.key === 'source') return false;
              if (hasCustomDest && col.key === 'destination') return false;
            }
            return true;
          });

        const preservedColumns = props.columns?.filter(col => {
          if (props.type === 'edge') {
            return col.key === sourceField || col.key === destField;
          }
          return essentialColumns.includes(col.key);
        }) || [];

        if (finalProcessedLabel.noInclusionMatches) {
          finalProcessedLabel = {
            ...finalProcessedLabel,
            columns: preservedColumns
          };
        } else {
          const mergedColumns = [...preservedColumns];
          finalProcessedLabel.columns.forEach(col => {
            if (!mergedColumns.find(c => c.key === col.key)) {
              mergedColumns.push(col);
            }
          });
          finalProcessedLabel = {
            ...finalProcessedLabel,
            columns: mergedColumns
          };
        }
      }

      return {
        ...props,
        title: finalProcessedLabel?.title || props.title,
        columns: finalProcessedLabel?.columns || props.columns,
        importantColumns: finalProcessedLabel?.columns || props.importantColumns,
        labels: props.processedEncodings?.labels || []
      };
    })
);

const WithSelectionContainer = container({
  renderLoading: false,
  fragment: () =>
    `{ type, index, title, globalIndex, columns, importantColumns, id, _id, name, identifier }`,
});

const BoundInspector = WithSelectionContainer(WithFilteredColumns(WithColorData(Inspector))); 

export const InspectorContainer = container({
  renderLoading: false,
  fragment: ({ selection } = {}) => `{
        propertiesEnabled,
        showActions,
        inspectorEnabled,
        expander: { open, hops, direction },
        selection: ${BoundInspector.fragment(selection)},
        encodings: {
          complex,
          point: { color, size, icon, fieldinclusion, fieldexclusion, computed, types, show, value }, 
          edge: { color, icon, fieldinclusion, fieldexclusion, computed, types, show, value }
        }
    }`,
  dispatchers: {
    onFilter: addFilter,
    onExclude: addExclusion,
    setExpanderOpen,
    setExpanderHops,
    setExpanderDirection,
    commitExpansion,
  },
})((props) => {
  const {selection, globalSelection, expander, inspectorEnabled, propertiesEnabled } = props;
  console.log(props, '@InspectorContainer');

  const points = globalSelection?.point ?? [];
  const edges = globalSelection?.edge ?? [];
  const selectionCount = points.length + edges.length;

  if (
    !inspectorEnabled ||
    (selectionCount === 1 && !selection) ||
    selectionCount === 0 ||
    propertiesEnabled
  ) {
    return null;
  }

  return selectionCount > 1 ? (
    <MultiInspector
      {...props}
      points={points}
      edges={edges}
      expander={{ ...expander }}
    />
  ) : (
    <BoundInspector {...props} data={selection} expander={{ ...expander }} />
  );
});
